import isEqual from 'hs-lodash/isEqual';
import { useCallback, useMemo, useRef } from 'react';
import { useSubscribableState } from '../../../common/hooks';
import { messages } from '../../../common/messages';
import { getDeferred } from '../../../common/utils';
export const getInitialComponentState = (config, portalId, enableScheduler) => Object.entries(config).reduce((state, [name, definition]) => {
  const {
    deferLoading = true,
    initiallyOpen = false,
    enableHubless,
    initiallyFullscreen = false
  } = definition;
  state[name] = {
    isLoaded: enableScheduler ? false : !deferLoading,
    isOpen: initiallyOpen && !deferLoading,
    style: {},
    isReady: false,
    isDisabled: !enableHubless && !portalId,
    isFullscreenMode: initiallyFullscreen
  };
  return state;
}, {});
export const useComponentStateManager = apiContext => {
  // Fallback in case initial state wasn't set with `setComponentState` in `onReady`
  const initialState = useMemo(() => getInitialComponentState(apiContext.current.appliedConfig, apiContext.current.portalId, apiContext.current.enableScheduler), [apiContext]);
  const [componentStatesMap, setComponentStatesMap, onComponentStatesMapChange, componentStatesMapRef] = useSubscribableState(initialState);
  const get = useCallback(key => name => componentStatesMapRef.current[name][key], [componentStatesMapRef]);
  const set = useCallback(key => (name, value) => {
    setComponentStatesMap(map => Object.assign({}, map, {
      [name]: Object.assign({}, map[name], {
        [key]: value
      })
    }));

    // Propagate updates to the component if component is already loaded
    const context = apiContext.current.iframeContextMap[name];
    if (context) {
      context.sendMessage(messages.SET_COMPONENT_STATE, {
        updates: {
          [key]: value
        },
        target: name
      });
    }
  }, [apiContext, setComponentStatesMap]);
  const getComponentState = useCallback(name => componentStatesMapRef.current[name], [componentStatesMapRef]);
  const setComponentState = useCallback((name, nextState) => {
    setComponentStatesMap(map => {
      const currentComponentState = map[name];
      const nextComponentState = typeof nextState === 'function' ? nextState(currentComponentState) : nextState;
      if (isEqual(currentComponentState, nextComponentState)) {
        return map;
      }

      // Propagate updates to the component if component is already loaded
      const context = apiContext.current.iframeContextMap[name];
      if (context) {
        context.sendMessage(messages.SET_COMPONENT_STATE, {
          updates: nextComponentState,
          target: name
        });
      }
      return Object.assign({}, map, {
        [name]: nextComponentState
      });
    });
  }, [apiContext, setComponentStatesMap]);
  const waitUntilReadyCache = useRef({});
  const waitUntilReady = useCallback(name => {
    if (componentStatesMapRef.current[name].isReady) {
      return Promise.resolve();
    }
    if (name in waitUntilReadyCache.current) {
      return waitUntilReadyCache.current[name];
    }
    const deferred = getDeferred();
    waitUntilReadyCache.current[name] = deferred.promise;
    const unsubscribe = onComponentStatesMapChange(isReady => {
      if (isReady) {
        deferred.resolve();
        unsubscribe();
      }
    }, ({
      [name]: {
        isReady
      }
    }) => isReady);
    return deferred.promise;
  }, [componentStatesMapRef, onComponentStatesMapChange]);
  const isOpen = useMemo(() => get('isOpen'), [get]);
  const setIsOpen = useMemo(() => set('isOpen'), [set]);
  const isLoaded = useMemo(() => get('isLoaded'), [get]);
  const setIsLoaded = useMemo(() => set('isLoaded'), [set]);
  const isReady = useMemo(() => get('isReady'), [get]);
  const setIsReady = useMemo(() => set('isReady'), [set]);
  const isDisabled = useMemo(() => get('isDisabled'), [get]);
  const getStyle = useMemo(() => get('style'), [get]);
  return useMemo(() => ({
    isLoaded,
    setIsLoaded,
    isOpen,
    setIsOpen,
    getStyle,
    isReady,
    setIsReady,
    isDisabled,
    getComponentState,
    setComponentState,
    onComponentStatesMapChange,
    componentStatesMap,
    waitUntilReady
  }), [isLoaded, setIsLoaded, isOpen, setIsOpen, getStyle, isReady, setIsReady, isDisabled, getComponentState, setComponentState, onComponentStatesMapChange, componentStatesMap, waitUntilReady]);
};